import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackLead } from "@/lib/analytics";
import {
  FormMountMark,
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
} from "@/lib/hubspot";

const TOTAL_QUESTIONS = 8;

function isValidEmail(email) {
  // Mirrors the validation in the original HTML artifact.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function computeScore(answers) {
  let score = 50;
  const signals = [];

  if (answers.q4 === "Yes, talked to real users and confirmed the problem") {
    score += 20;
    signals.push({ label: "Demand validated with users", type: "good" });
  } else if (
    answers.q4 === "Somewhat (done some research but no direct user interviews)"
  ) {
    score += 8;
    signals.push({ label: "Partial market research done", type: "warn" });
  } else {
    signals.push({ label: "Demand not yet validated", type: "warn" });
  }

  if (answers.q5 === "Funded and ready to build") {
    score += 15;
    signals.push({ label: "Funding secured", type: "good" });
  } else if (answers.q5 === "I have wireframes or a prototype") {
    score += 8;
    signals.push({ label: "Concept visualized", type: "good" });
  }

  if (["$75K – $200K", "$200K+"].includes(answers.q7)) {
    score += 10;
    signals.push({ label: "Adequate budget range", type: "good" });
  } else if (answers.q7 === "$25K – $75K") {
    score += 5;
    signals.push({ label: "MVP-viable budget", type: "good" });
  } else {
    signals.push({ label: "Budget may need refinement", type: "warn" });
  }

  if (answers.q6 === "Launch an MVP and get first users") {
    signals.push({ label: "Clear launch objective", type: "good" });
    score += 5;
  }

  score = Math.min(score, 97);
  return { score, signals: signals.slice(0, 4) };
}

function formatIdeaResults(scored, questions, answers) {
  const lines = [`Viability score: ${scored.score}/100`];
  if (scored.signals?.length) {
    lines.push(
      `Signals: ${scored.signals.map((signal) => `${signal.label} (${signal.type})`).join("; ")}`,
    );
  }
  questions.forEach((question, index) => {
    const value = answers[question.key];
    if (value) lines.push(`Q${index + 1} ${question.headline}: ${value}`);
  });
  return lines.join("\n");
}

export default function IdeaValidatorModal({
  open,
  onOpenChange,
  initialFirstName = "",
  initialStep = 0,
  page = null,
}) {
  const slugify = (value) => {
    return String(value)
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const [step, setStep] = useState(0); // 0 intro, 1..8 questions, 9 email capture, 10 results
  const [answers, setAnswers] = useState({});
  const [capture, setCapture] = useState({
    firstName: initialFirstName,
    email: "",
    company: "",
  });
  const [result, setResult] = useState({ score: null, signals: [] });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const captureMountedAt = useRef(null);

  useEffect(() => {
    if (!open) return;

    // Reset flow when opening (consistent with "reset-on-close" in the plan).
    setStep(initialStep);
    setAnswers({});
    setCapture({ firstName: initialFirstName || "", email: "", company: "" });
    setResult({ score: null, signals: [] });
    setSubmitting(false);
    setSubmitError("");
    setCompanyUrl("");
    captureMountedAt.current = null;
  }, [open, initialFirstName, initialStep]);

  const progressPct = useMemo(() => {
    if (step === 0) return 0;
    if (step >= 1 && step <= TOTAL_QUESTIONS) {
      return (step / TOTAL_QUESTIONS) * 100;
    }
    if (step === TOTAL_QUESTIONS + 1) return 100; // email capture is the final step before results
    return 100; // results
  }, [step]);

  const questions = useMemo(
    () => [
      {
        key: "q1",
        eyebrow: "Question 1 of 8",
        headline: "What best describes your app idea?",
        sub: "This helps us tailor your report to your specific space.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "Consumer / Lifestyle",
            icon: "📱",
            label: "Consumer / Lifestyle",
            desc: "Wellness, productivity, social, entertainment",
          },
          {
            value: "Healthcare / MedTech",
            icon: "🏥",
            label: "Healthcare / MedTech",
            desc: "Telehealth, patient care, clinical tools",
          },
          {
            value: "SaaS / B2B Tool",
            icon: "⚙️",
            label: "SaaS / B2B Tool",
            desc: "Software for businesses, dashboards, platforms",
          },
          {
            value: "Fintech / Finance",
            icon: "💳",
            label: "Fintech / Finance",
            desc: "Banking, payments, investments, budgeting",
          },
          {
            value: "Field / Operations",
            icon: "🏗️",
            label: "Field / Operations",
            desc: "Logistics, field service, inspections, reporting",
          },
          {
            value: "Other / Not Sure Yet",
            icon: "💡",
            label: "Other / Not Sure Yet",
            desc: "Still forming the concept",
          },
        ],
      },
      {
        key: "q2",
        eyebrow: "Question 2 of 8",
        headline: "Who is your primary user?",
        sub: "Understanding your user shapes everything from features to go-to-market strategy.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "Consumers (B2C)",
            icon: "👤",
            label: "Consumers (B2C)",
            desc: "General public, individual users",
          },
          {
            value: "Businesses (B2B)",
            icon: "🏢",
            label: "Businesses (B2B)",
            desc: "Companies, teams, enterprise clients",
          },
          {
            value: "Internal / Employees",
            icon: "🔧",
            label: "Internal / Employees",
            desc: "Field staff, operations teams, internal tools",
          },
          {
            value: "Both (B2B + B2C)",
            icon: "🔄",
            label: "Both (B2B + B2C)",
            desc: "Marketplace or multi-sided platform",
          },
        ],
      },
      {
        key: "q3",
        eyebrow: "Question 3 of 8",
        headline: "What platform are you targeting?",
        sub: "Platform choice affects timeline, cost, and audience reach.",
        gridClass: "grid-cols-1 sm:grid-cols-3",
        options: [
          {
            value: "iOS (iPhone/iPad)",
            icon: "🍎",
            label: "iOS",
            desc: "iPhone & iPad",
          },
          {
            value: "Android",
            icon: "🤖",
            label: "Android",
            desc: "Google Play",
          },
          {
            value: "Both iOS & Android",
            icon: "📲",
            label: "Both",
            desc: "iOS & Android",
          },
          {
            value: "Web App / PWA",
            icon: "🌐",
            label: "Web App",
            desc: "Browser / PWA",
          },
          {
            value: "Mobile + Web",
            icon: "🖥️",
            label: "Mobile + Web",
            desc: "Full cross-platform",
          },
          {
            value: "Not Sure Yet",
            icon: "🤔",
            label: "Not Sure",
            desc: "Need guidance",
          },
        ],
      },
      {
        key: "q4",
        eyebrow: "Question 4 of 8",
        headline: "Have you validated demand for your idea?",
        sub: "Honest here (validation is one of the biggest predictors of launch success).",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "Yes, talked to real users and confirmed the problem",
            icon: "✅",
            label: "Yes, confirmed with real users",
            desc: "I've spoken to potential customers and validated the problem",
          },
          {
            value:
              "Somewhat (done some research but no direct user interviews)",
            icon: "🔍",
            label: "Somewhat (done research, no user interviews yet)",
            desc: "I've researched the market but haven't spoken to users directly",
          },
          {
            value: "Not yet, but I believe strongly in the idea",
            icon: "💭",
            label: "Not yet, but I believe strongly in the idea",
            desc: "Haven't validated externally yet",
          },
        ],
      },
      {
        key: "q5",
        eyebrow: "Question 5 of 8",
        headline: "What stage are you at right now?",
        sub: "This tells us how to calibrate your report and recommended path forward.",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "Just an idea in my head",
            icon: "💡",
            label: "Just an idea in my head",
            desc: "No documentation or designs yet",
          },
          {
            value: "I have wireframes or a prototype",
            icon: "🎨",
            label: "I have wireframes or a prototype",
            desc: "Some visual concept exists",
          },
          {
            value: "Funded and ready to build",
            icon: "💰",
            label: "Funded and ready to build",
            desc: "Budget is secured, need the right team",
          },
          {
            value: "Already have an app that needs work",
            icon: "🔄",
            label: "Already have an app that needs work",
            desc: "Rebuild, modernize, or add features",
          },
        ],
      },
      {
        key: "q6",
        eyebrow: "Question 6 of 8",
        headline: "What's your primary goal in the next 6 months?",
        sub: "Understanding your goal helps us recommend the right approach and milestones.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "Launch an MVP and get first users",
            icon: "🚀",
            label: "Launch MVP & get first users",
            desc: "Ship fast, learn, iterate",
          },
          {
            value: "Raise funding with a working demo",
            icon: "📈",
            label: "Raise funding with a demo",
            desc: "Impress investors with a working product",
          },
          {
            value: "Replace or modernize an existing system",
            icon: "🔧",
            label: "Replace / modernize existing system",
            desc: "Legacy modernization or rebuild",
          },
          {
            value: "Scale an existing product to more users",
            icon: "⚡",
            label: "Scale to more users",
            desc: "Performance, infrastructure, growth",
          },
        ],
      },
      {
        key: "q7",
        eyebrow: "Question 7 of 8",
        headline: "What's your estimated budget range?",
        sub: "Be as honest as you can. This helps us recommend the right scope and approach.",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "Under $25K",
            icon: "💵",
            label: "Under $25K",
            desc: "Tight budget (likely MVP or proof of concept)",
          },
          {
            value: "$25K – $75K",
            icon: "💰",
            label: "$25K – $75K",
            desc: "Mid-range (solid MVP with core features)",
          },
          {
            value: "$75K – $200K",
            icon: "🏦",
            label: "$75K – $200K",
            desc: "Growth budget (full-featured product launch)",
          },
          {
            value: "$200K+",
            icon: "🚀",
            label: "$200K+",
            desc: "Enterprise-scale (complex platform or system)",
          },
        ],
      },
      {
        key: "q8",
        eyebrow: "Question 8 of 8",
        headline: "What's your target launch timeline?",
        sub: "This helps us identify the right team size and delivery approach for your situation.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "ASAP (within 3 months)",
            icon: "🔥",
            label: "ASAP (within 3 months)",
            desc: "Urgent timeline, speed is the priority",
          },
          {
            value: "3–6 months",
            icon: "📅",
            label: "3–6 months",
            desc: "Steady pace with proper planning",
          },
          {
            value: "6–12 months",
            icon: "🗓️",
            label: "6–12 months",
            desc: "More thorough build with full feature set",
          },
          {
            value: "Flexible (quality over speed)",
            icon: "🎯",
            label: "Flexible (quality over speed)",
            desc: "No hard deadline, doing it right matters more",
          },
        ],
      },
    ],
    []
  );

  // Submit questions in the same order as the user experiences them (Q1..Q8).
  const labelQuestionOrder = questions;

  const qStepIndex = step - 1; // step 1 -> q1 index 0
  const currentQuestion = step >= 1 && step <= TOTAL_QUESTIONS ? questions[qStepIndex] : null;

  const captureValid = capture.firstName.trim().length > 0 && isValidEmail(capture.email);

  const handleCaptureSubmit = async (event) => {
    event.preventDefault();
    if (!captureValid || submitting) return;
    setSubmitError("");
    const scored = computeScore(answers);
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: capture.email,
          name: capture.firstName,
          company: capture.company,
          source: "app_idea_validator",
          budget: answers.q7 || "",
          landingTitle: page?.title || "",
          landingSlug: page?.slug || "",
          results: formatIdeaResults(scored, questions, answers),
        },
        {
          companyUrl: honeypotValue(event, companyUrl),
          mountedAt: captureMountedAt.current,
          name: capture.firstName,
        },
      );
      if (outcome === "sent") trackLead("app_idea_validator");
      setResult(scored);
      setStep(10);
    } catch {
      setSubmitError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden bg-[#060b14] border border-white/10">
        <div className="w-full max-w-[680px] mx-auto px-5 sm:px-6 py-8 pb-10">
          <div className="h-[3px] bg-[#162033] rounded-[2px] mb-8 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] transition-[width] duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {/* SCREEN 0: Intro */}
          {step === 0 && (
            <div>
              <div className="inline-flex items-center gap-2 bg-[#00AEEF]/10 border border-[#00AEEF]/30 rounded-full px-4 py-[6px] text-[12px] font-medium text-[#06B6D4] tracking-[0.5px] mb-6">
                Free App Idea Assessment
              </div>
              <h2 className="font-bold text-[clamp(28px,5vw,44px)] leading-[1.1] mb-5 text-white">
                How Good is Your
                <br />
                <span className="bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] bg-clip-text text-transparent">
                  App Idea?
                </span>
              </h2>
              <p className="text-[#7a90b0] text-[15px] leading-relaxed mb-10">
                Answer 8 quick questions and we'll send you a personalized report assessing your idea's viability, market fit, and recommended next steps, from senior engineers shipping since 2002.
              </p>

              <div className="flex flex-wrap gap-7 mb-10">
                <div className="flex items-center gap-2 text-[#7a90b0] text-[13px]">
                  <span className="text-[16px]">⏱</span>
                  <span>Takes about 2 minutes</span>
                </div>
                <div className="flex items-center gap-2 text-[#7a90b0] text-[13px]">
                  <span className="text-[16px]">📊</span>
                  <span>Free personalized report</span>
                </div>
                <div className="flex items-center gap-2 text-[#7a90b0] text-[13px]">
                  <span className="text-[16px]">🔒</span>
                  <span>No spam, ever</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={() => setStep(1)}
                className="bg-gradient-to-r from-[#00AEEF] to-[#2563eb] hover:from-[#00AEEF] hover:to-[#1d4ed8] text-white font-bold px-10 py-5 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)]"
              >
                Start Your Free Assessment →
              </Button>
            </div>
          )}

          {/* Question screens */}
          {currentQuestion && (
            <div>
              <div className="text-[12px] font-medium tracking-[2px] uppercase text-[#06B6D4] mb-4">
                {currentQuestion.eyebrow}
              </div>
              <h3 className="text-white font-bold text-[clamp(22px,3.6vw,30px)] leading-tight mb-2">
                {currentQuestion.headline}
              </h3>
              <p className="text-[#7a90b0] text-[15px] leading-relaxed mb-8">
                {currentQuestion.sub}
              </p>

              <div className={`grid gap-3 ${currentQuestion.gridClass} mb-8`}>
                {currentQuestion.options.map((opt) => {
                  const selected = answers[currentQuestion.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAnswers((prev) => ({ ...prev, [currentQuestion.key]: opt.value }))}
                      className={[
                        "bg-[#0e1825] border border-white/10 rounded-[12px] p-[18px_16px] text-left cursor-pointer transition-all duration-200 flex items-start gap-3 relative overflow-hidden select-none",
                        selected
                          ? "border-[#3b82f6] bg-[#1a2d47] shadow-[0_0_0_1px_rgba(59,130,246,0.3),0_4px_20px_rgba(59,130,246,0.15)]"
                          : "hover:border-[#00AEEF]/40 hover:translate-y-[-1px] hover:bg-[#1a2d47]",
                      ].join(" ")}
                    >
                      <div className="text-[24px] leading-none flex-shrink-0 mt-0.5">
                        {opt.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-[14px] text-white mb-[3px]">
                          {opt.label}
                        </div>
                        <div className="text-[12px] text-[#7a90b0] leading-relaxed">
                          {opt.desc}
                        </div>
                      </div>
                      <div
                        className={[
                          "w-[18px] h-[18px] rounded-full border border-white/10 ml-auto flex items-center justify-center mt-0.5 transition-all duration-200",
                          selected ? "bg-[#00AEEF] border-[#00AEEF]" : "bg-transparent",
                        ].join(" ")}
                      >
                        <span className={selected ? "text-white text-[10px] opacity-100" : "text-white text-[10px] opacity-0"}>
                          ✓
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div
                className={
                  step === 1
                    ? "flex items-center justify-center"
                    : "flex items-center justify-between gap-4"
                }
              >
                {step > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(step - 1)}
                    className="bg-transparent text-[#7a90b0] hover:text-white hover:bg-transparent px-0 py-0 h-auto"
                  >
                    ← Back
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={!answers[currentQuestion.key]}
                  className="bg-gradient-to-r from-[#00AEEF] to-[#2563eb] text-white font-bold px-10 py-4 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue →
                </Button>
              </div>
            </div>
          )}

          {/* SCREEN 9: Email capture */}
          {step === TOTAL_QUESTIONS + 1 && (
            <form
              name="idea-validator"
              data-form-name="App Idea Assessment"
              data-hs-do-not-collect="true"
              aria-busy={submitting}
              onSubmit={handleCaptureSubmit}
            >
              <FormMountMark mountedAtRef={captureMountedAt} />
              <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
              <input type="hidden" name="formName" value="App Idea Assessment" />

              {/* Quiz answers are copied into the HubSpot message. */}
              {labelQuestionOrder.map((q, idx) => (
                <input
                  key={q.key}
                  className="sr-only"
                  type="text"
                  name={`question_${idx + 1}_${slugify(q.headline)}`}
                  value={answers[q.key] || ""}
                  readOnly
                />
              ))}
              {/* Compatibility: some HubSpot forms expect `firstName` even if we label the field as Full Name. */}
              <input className="sr-only" type="text" name="firstName" value={capture.firstName || ""} readOnly />

              <div>
                <div className="text-[12px] font-medium tracking-[2px] uppercase text-[#06B6D4] mb-4">
                  Almost there
                </div>
                <h3 className="text-white font-bold text-[clamp(22px,3.6vw,30px)] leading-tight mb-2">
                  Where should we send your report?
                </h3>
                <p className="text-[#7a90b0] text-[15px] leading-relaxed mb-8">
                  We'll analyze your answers and send you a personalized App Idea Assessment, including viability signals, recommended platform approach, and suggested first steps.
                </p>

                <div className="space-y-5 mb-8">
                  <div>
                    <div className="text-[13px] font-medium text-[#7a90b0] mb-2 tracking-[0.3px]">
                      Full Name
                    </div>
                    <Input
                      name="name"
                      value={capture.firstName}
                      onChange={(e) =>
                        setCapture((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      placeholder="John Smith"
                      className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                    />
                  </div>

                  <div>
                    <div className="text-[13px] font-medium text-[#7a90b0] mb-2">
                      Work Email
                    </div>
                    <Input
                      name="email"
                      value={capture.email}
                      onChange={(e) =>
                        setCapture((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      placeholder="you@company.com"
                      type="email"
                      className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                    />
                  </div>

                  <div>
                    <div className="text-[13px] font-medium text-[#7a90b0] mb-2">
                      Company / Project Name{" "}
                      <span className="text-[#7a90b0] font-light">(optional)</span>
                    </div>
                    <Input
                      name="company"
                      value={capture.company}
                      onChange={(e) =>
                        setCapture((prev) => ({
                          ...prev,
                          company: e.target.value,
                        }))
                      }
                      placeholder="Acme Corp"
                      className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                    />
                  </div>
                </div>

                <LeadFormError message={submitError} testId="idea-validator-error" />
                <Button
                  type="submit"
                  disabled={!captureValid || submitting}
                  className="w-full bg-gradient-to-r from-[#00AEEF] to-[#2563eb] text-white font-bold rounded-[10px] px-8 py-5 shadow-[0_4px_20px_rgba(0,174,239,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Me My Free Report →"}
                </Button>

                <p className="text-[12px] text-[#7a90b0] mt-4 leading-relaxed text-center">
                  By submitting, you agree to receive your assessment report and occasional updates from Maxiom Apps. No spam, ever.{" "}
                  <a
                    href="https://www.maxiomtech.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#06B6D4] hover:underline"
                  >
                    Privacy Policy
                  </a>
                </p>

                <div className="flex justify-start mt-5">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(8)}
                    className="bg-transparent text-[#7a90b0] hover:text-white hover:bg-transparent px-0 py-0 h-auto"
                  >
                    ← Back
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* SCREEN 10: Results */}
          {step === 10 && (
            <div>
              <div className="text-center pb-8">
                <div className="w-[72px] h-[72px] mx-auto bg-gradient-to-r from-[#10b981]/20 to-[#06b6d4]/20 border border-[#10b981]/40 rounded-full flex items-center justify-center text-[30px] animate-pulse mb-6">
                  🎉
                </div>
                <h3 className="font-extrabold text-[clamp(24px,4vw,34px)] leading-tight text-white mb-4">
                  Your idea is showing{" "}
                  <span className="bg-gradient-to-r from-[#3b82f6] to-[#06b6d4] bg-clip-text text-transparent">
                    strong signals.
                  </span>
                </h3>
                <p className="text-[16px] text-[#7a90b0] leading-relaxed max-w-[480px] mx-auto">
                  We're preparing your personalized App Idea Assessment. Expect it in your inbox within 1 business day from our team.
                </p>
              </div>

              <div className="bg-[#0e1825] border border-white/10 rounded-[12px] p-7 mb-6">
                <div className="flex justify-between text-[13px] text-[#7a90b0] mb-3">
                  <span>Idea Viability Score</span>
                  <strong className="text-white font-semibold">{result.score !== null ? `${result.score}/100` : "—"}</strong>
                </div>

                <div className="h-[8px] bg-[#162033] rounded-[4px] overflow-hidden mb-5">
                  <div
                    className="h-full bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] transition-[width] duration-1000 ease-out"
                    style={{ width: `${result.score ?? 0}%` }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {result.signals.map((s) => (
                    <div key={s.label} className="flex items-center gap-2 text-[13px] text-[#7a90b0]">
                      <div className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${s.type === "warn" ? "bg-[#f59e0b]" : "bg-[#10b981]"}`} />
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#00AEEF]/10 to-[#06B6D4]/5 border border-[#00AEEF]/20 rounded-[14px] p-7 text-center mb-5">
                <h3 className="font-bold text-[18px] text-white mb-2">Want to talk sooner?</h3>
                <p className="text-[14px] text-[#7a90b0] mb-5 leading-relaxed">
                  Skip the wait. Book a free 30-minute strategy call with one of our senior engineers and we'll walk through your idea live.
                </p>
                <a
                  href="https://maxiomapps.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    type="button"
                    className="bg-gradient-to-r from-[#00AEEF] to-[#2563eb] text-white font-bold px-10 py-4 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)]"
                  >
                    Schedule a Free Call →
                  </Button>
                </a>
              </div>

              <div className="border-t border-white/10 mt-6 pt-5 text-center">
                <p className="text-[13px] text-[#7a90b0]">
                  Questions? Email us at{" "}
                  <a
                    href="mailto:hello@maxiomtech.com"
                    className="text-[#06B6D4] hover:underline"
                  >
                    hello@maxiomtech.com
                  </a>{" "}
                  or call{" "}
                  <a
                    href="tel:7039429420"
                    className="text-[#06B6D4] hover:underline"
                  >
                    703-942-9420
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

